import { defineStore } from 'pinia'
import { authApi } from '../api/auth.api'
import { apiErrorMessage } from '../services/utils'
import type { AuthState, AuthTokens, CreateUserPayload } from '../types/auth'

export type LoginResult = { ok: true } | { ok: false; error: string }

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    userEmail: null,
    accessToken: localStorage.getItem('access_token'),
    isAuthenticated: !!localStorage.getItem('access_token'),
  }),

  actions: {
    async checkEmailAvailable(email: string): Promise<boolean> {
      try {
        const response = await authApi.checkEmailAvailable(email)
        if (response?.status === 200) {
          this.userEmail = email.trim()
          return true
        }
        return false
      } catch (error: any) {
        if (error.response?.status === 409) {
          return false
        }
        throw error
      }
    },

    async requestOtp(email: string): Promise<any> {
      try {
        const response = await authApi.sendCode(email)
        this.userEmail = email
        return response
      } catch (error: any) {
        if (error.response?.status === 400) {
          return false
        }
        throw error
      }
    },

    async confirmOtp(code: string): Promise<boolean> {
      if (!this.userEmail) {
        throw new Error('User email is not set')
      }

      try {
        const response = await authApi.verifyCode(this.userEmail, code)
        return response.status === 200
      } catch (error: any) {
        if (error.response?.status === 400) {
          return false
        }
        throw error
      }
    },
    async register(payload: CreateUserPayload): Promise<void> {
      if (!this.userEmail) {
        throw new Error('User email is not set')
      }

      const registerPayload = {
        first_name: payload.first_name,
        last_name: payload.last_name,
        password: payload.password,
        connect_country: payload.connect_country,
      }
      const response = await authApi.register(this.userEmail, registerPayload)
      this.setAuth(response)
    },

    async login(email: string, password: string): Promise<LoginResult> {
      try {
        const data = await authApi.login(email, password)
        if (data?.access) {
          this.setAuth(data)
          return { ok: true }
        }
        return { ok: false, error: 'No active account found with the given credentials.' }
      } catch (e: unknown) {
        const msg = apiErrorMessage(e, '')
        return {
          ok: false,
          error: msg || 'No active account found with the given credentials.',
        }
      }
    },

    logout(): void {
      this.user = null
      this.userEmail = null
      this.accessToken = null
      this.isAuthenticated = false
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
    },

    async fetchMe(): Promise<boolean> {
      try {
        const user = await authApi.me()
        this.user = user
        this.isAuthenticated = true
        return true
      } catch (error: any) {
        // access истёк или невалиден
        this.logout()
        return false
      }
    },
    async updateProfile(payload: { username?: string; first_name?: string; last_name?: string }) {
      const formData = new FormData()
      if (payload.username !== undefined) formData.append('username', payload.username)
      if (payload.first_name !== undefined) formData.append('first_name', payload.first_name)
      if (payload.last_name !== undefined) formData.append('last_name', payload.last_name)
      await authApi.updateMe(formData)
    },

    async updateProfileSettings(payload: Record<string, string | number | boolean | null | undefined>) {
      await authApi.updateProfileSettings(payload)
      await this.fetchMe()
    },

    async verifySocialUrl(url: string): Promise<boolean> {
      const res = await authApi.verifySocialUrl(url)
      return !!res?.ok
    },

    async updateAvatar(formData: FormData) {
      await authApi.updateAvatar(formData)
    },

    async removeAvatar() {
      await authApi.removeAvatar()

      if (this.user) {
        this.user.avatar = null
      }
    },

    /** Create Stripe Connect account and refresh user (stripe_connect_account_id). */
    async createConnectAccount(country?: string): Promise<{ account_id: string } | null> {
      try {
        const data = await authApi.createConnectAccount(country)
        await this.fetchMe()
        return data
      } catch (_e) {
        return null
      }
    },

    /** Refresh user after verifying Stripe Connect onboarding with Stripe API. */
    async refreshConnectOnboardingStatus(): Promise<boolean> {
      try {
        const status = await authApi.getConnectStatus()
        if (this.user) {
          this.user = {
            ...this.user,
            stripe_connect_account_id: status.has_account
              ? (status.account_id ?? this.user.stripe_connect_account_id)
              : null,
            stripe_connect_onboarding_complete: status.stripe_verified || status.onboarding_complete,
          }
        }
        return status.stripe_verified || status.onboarding_complete
      } catch (_e) {
        return false
      }
    },

    /** Get Stripe onboarding URL; user is redirected there to complete DOB, phone, address, identity. */
    async getConnectOnboardingLink(
      returnUrl: string,
      refreshUrl: string,
    ): Promise<{ url: string | null; error?: string; staleAccount?: boolean }> {
      try {
        const data = await authApi.getConnectOnboardingLink(returnUrl, refreshUrl)
        const url = data?.url?.trim() || null
        if (!url) {
          return { url: null, error: 'Stripe did not return an onboarding link. Please try again.' }
        }
        return { url }
      } catch (e: unknown) {
        const err = e as { response?: { data?: { code?: string; detail?: string } } }
        if (err.response?.data?.code === 'connect_account_stale') {
          await this.refreshConnectOnboardingStatus()
          return {
            url: null,
            staleAccount: true,
            error: err.response?.data?.detail || 'Your Stripe connection was reset. Please connect Stripe again.',
          }
        }
        return {
          url: null,
          error: apiErrorMessage(e, 'Could not open setup form. Please try again.'),
        }
      }
    },

    /** Get client_secret for Stripe Connect embedded onboarding (iframe). */
    async getConnectAccountSession(): Promise<string | null> {
      try {
        const data = await authApi.getConnectAccountSession()
        return data?.client_secret ?? null
      } catch (_e) {
        return null
      }
    },

    /** Get Stripe Connect account balance. */
    async getConnectBalance(): Promise<{
      available: Array<{ amount: number; currency: string }>
      pending: Array<{ amount: number; currency: string }>
    } | null> {
      try {
        return await authApi.getConnectBalance()
      } catch (_e) {
        return null
      }
    },

    /** Create a manual payout (amount in cents). Requires account password. */
    async createPayout(
      amount: number,
      password: string,
      currency?: string,
      forceInstant?: boolean,
    ): Promise<{ id: string; amount: number; currency: string; status: string }> {
      return authApi.createPayout(amount, password, currency, forceInstant)
    },

    setAuth(tokens: AuthTokens): void {
      if (!tokens?.access || !tokens?.refresh) {
        throw new Error('Invalid auth tokens')
      }

      this.accessToken = tokens.access
      this.isAuthenticated = true

      localStorage.setItem('access_token', tokens.access)
      localStorage.setItem('refresh_token', tokens.refresh)
    },
  },
})
